import React from 'react';
import { Icon } from '@iconify/react';

/**
 * ThreadsMobilePreview - Threads-style mobile mockup component
 *
 * Features:
 * - Dark theme mobile device frame (1320x2868 - 19.5:9 ratio)
 * - Dynamic Island status bar
 * - Threads-style header with back button
 * - Profile section with avatar and username
 * - Image/media display area
 * - Interaction bar (like, comment, share, save)
 * - Post caption with account name
 * - Bottom navigation bar
 * - Edit Post and Publish Now buttons
 *
 * @param {object} props
 * @param {string} props.content - Post caption/content text
 * @param {string} props.accountName - Account name to display
 * @param {string} props.accountAvatar - Optional account avatar URL
 * @param {string} props.imageUrl - Post image URL
 * @param {number} props.likes - Number of likes (default: 0)
 * @param {number} props.comments - Number of comments (default: 1)
 * @param {boolean} props.showButtons - Show Edit/Publish buttons (default: true)
 * @param {function} props.onEdit - Callback for Edit Post button
 * @param {function} props.onPublish - Callback for Publish Now button
 * @param {boolean} props.loading - Whether content is loading
 */
function ThreadsMobilePreview({
  content = '',
  accountName = 'viralarc.ai',
  accountAvatar = null,
  imageUrl = null,
  likes = 0,
  comments = 1,
  showButtons = true,
  onEdit = () => {},
  onPublish = () => {},
  loading = false
}) {
  // Process content to handle formatting
  const processContent = (text) => {
    if (!text) return '';
    return text.trim();
  };

  const displayContent = processContent(content);

  // Detect if mobile
  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 768);

  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: isMobile ? 'flex-start' : 'flex-start',
      padding: isMobile ? '0' : '2rem',
      minHeight: '100%',
      overflow: 'hidden',
      gap: '1.5rem'
    }}>
      {/* iPhone Device Frame - 9:16 ratio for mobile, scaled down for desktop */}
      <div style={{
        width: isMobile ? '100%' : '360px',
        height: isMobile ? '100%' : '640px',
        background: isMobile ? 'transparent' : 'linear-gradient(145deg, #2c2c2e 0%, #1c1c1e 100%)',
        borderRadius: isMobile ? '0' : '40px',
        padding: isMobile ? '0' : '12px',
        boxShadow: isMobile ? 'none' : '0 20px 70px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.1) inset',
        position: 'relative',
        border: isMobile ? 'none' : '2px solid #3a3a3c',
        transform: 'scale(1)',
        transformOrigin: 'center'
      }}>
        {/* Dynamic Island - Only show on desktop */}
        {!isMobile && (
          <div style={{
            position: 'absolute',
            top: '10px',
            left: '50%',
            transform: 'translateX(-50%)',
            width: '107px',
            height: '31px',
            background: '#000000',
            borderRadius: '16px',
            zIndex: 10,
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.5)'
          }} />
        )}

        {/* Screen */}
        <div style={{
          width: '100%',
          height: '100%',
          background: '#000000',
          borderRadius: '40px',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}>
          {/* Status Bar */}
          <div style={{
            height: '54px',
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'space-between',
            padding: '0 1.5rem 0.5rem',
            color: '#ffffff'
          }}>
            <span style={{ fontSize: '15px', fontWeight: '600', letterSpacing: '-0.3px' }}>1:36</span>
            <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
              {/* Signal Strength - 4 bars */}
              <svg width="18" height="12" viewBox="0 0 18 12" fill="none" style={{ marginTop: '1px' }}>
                <rect x="0" y="8" width="3" height="4" rx="0.5" fill="white"/>
                <rect x="5" y="6" width="3" height="6" rx="0.5" fill="white"/>
                <rect x="10" y="3" width="3" height="9" rx="0.5" fill="white"/>
                <rect x="15" y="0" width="3" height="12" rx="0.5" fill="white"/>
              </svg>

              {/* WiFi Icon */}
              <svg width="16" height="12" viewBox="0 0 16 12" fill="none">
                <path d="M8 11.5C8.828 11.5 9.5 10.828 9.5 10C9.5 9.172 8.828 8.5 8 8.5C7.172 8.5 6.5 9.172 6.5 10C6.5 10.828 7.172 11.5 8 11.5Z" fill="white"/>
                <path d="M8 5.5C10.21 5.5 12.219 6.39 13.657 7.828L15.071 6.414C13.218 4.561 10.709 3.5 8 3.5C5.291 3.5 2.782 4.561 0.929 6.414L2.343 7.828C3.781 6.39 5.79 5.5 8 5.5Z" fill="white"/>
                <path d="M8 0.5C11.588 0.5 14.924 1.898 17.485 4.243L16.071 5.657C13.925 3.683 11.081 2.5 8 2.5C4.919 2.5 2.075 3.683 -0.071 5.657L-1.485 4.243C1.076 1.898 4.412 0.5 8 0.5Z" fill="white" opacity="0"/>
              </svg>

              {/* Battery Icon with percentage */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                <svg width="27" height="13" viewBox="0 0 27 13" fill="none">
                  {/* Battery outline */}
                  <rect x="0.5" y="0.5" width="22" height="12" rx="2.5" stroke="white" strokeOpacity="0.4" fill="none"/>
                  {/* Battery fill (66%) */}
                  <rect x="2" y="2" width="14.5" height="9" rx="1.5" fill="#34C759"/>
                  {/* Battery tip */}
                  <rect x="23.5" y="4.5" width="2.5" height="4" rx="0.5" fill="white" fillOpacity="0.4"/>
                </svg>
                <span style={{
                  fontSize: '11px',
                  fontWeight: '600',
                  color: 'white',
                  marginLeft: '2px'
                }}>66%</span>
              </div>
            </div>
          </div>

          {/* Header with Back Button */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0.75rem 1.25rem',
            borderBottom: '0.5px solid rgba(255, 255, 255, 0.1)',
            background: '#000000'
          }}>
            <Icon icon="mdi:arrow-left" style={{ fontSize: '28px', color: '#ffffff' }} />
            <div style={{
              color: '#ffffff',
              fontSize: '16px',
              fontWeight: '600',
              letterSpacing: '-0.2px',
              textAlign: 'center',
              flex: 1
            }}>
              {accountName}
              <div style={{
                fontSize: '14px',
                fontWeight: '400',
                color: '#a8a8a8',
                marginTop: '2px'
              }}>
                Posts
              </div>
            </div>
            <div style={{ width: '28px' }} /> {/* Spacer for centering */}
          </div>

          {/* Content Area - Scrollable */}
          <div style={{
            flex: 1,
            overflowY: 'auto',
            background: '#000000'
          }}>
            {/* Profile Section */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              padding: '1rem 1.25rem 0.75rem',
              gap: '0.75rem'
            }}>
              {/* Avatar */}
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                background: accountAvatar
                  ? `url(${accountAvatar})`
                  : 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                border: '2px solid rgba(255, 255, 255, 0.15)',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '18px',
                fontWeight: '600',
                color: '#ffffff'
              }}>
                {!accountAvatar && accountName.charAt(0).toUpperCase()}
              </div>

              {/* Account Name and Time */}
              <div style={{ flex: 1 }}>
                <div style={{
                  color: '#ffffff',
                  fontSize: '15px',
                  fontWeight: '600',
                  letterSpacing: '-0.2px',
                  marginBottom: '2px'
                }}>
                  {accountName}
                </div>
                <div style={{
                  color: '#a8a8a8',
                  fontSize: '13px',
                  letterSpacing: '-0.1px'
                }}>
                  5 days ago
                  <span style={{ margin: '0 0.35rem' }}>·</span>
                  <span style={{ color: '#8b8b8b' }}>tate, Elias · Xoyo</span>
                </div>
              </div>

              {/* More Options */}
              <Icon icon="mdi:dots-horizontal" style={{ fontSize: '24px', color: '#ffffff' }} />
            </div>

            {/* Image/Media Section */}
            {loading ? (
              <div style={{
                width: '100%',
                height: '400px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: '#1a1a1a'
              }}>
                <Icon icon="mdi:loading" className="spin" style={{ fontSize: '48px', color: '#a8a8a8' }} />
              </div>
            ) : imageUrl ? (
              <div style={{
                width: '100%',
                aspectRatio: '1/1',
                background: `url(${imageUrl})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                marginTop: '0.5rem'
              }} />
            ) : (
              <div style={{
                width: '100%',
                height: '400px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'linear-gradient(135deg, #e0c3fc 0%, #8ec5fc 100%)',
                marginTop: '0.5rem'
              }}>
                <Icon icon="mdi:instagram" style={{ fontSize: '120px', color: 'rgba(255, 255, 255, 0.8)' }} />
              </div>
            )}

            {/* Insights and Boost Section */}
            <div style={{
              padding: '0.75rem 1.25rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '1rem'
            }}>
              <button style={{
                background: 'transparent',
                border: 'none',
                color: '#4a9eff',
                fontSize: '15px',
                fontWeight: '600',
                cursor: 'pointer',
                padding: 0
              }}>
                View insights
              </button>

              <button style={{
                background: '#4a9eff',
                border: 'none',
                color: '#ffffff',
                fontSize: '15px',
                fontWeight: '600',
                cursor: 'pointer',
                padding: '0.5rem 1.5rem',
                borderRadius: '8px'
              }}>
                Boost post
              </button>
            </div>

            {/* Post Content/Caption */}
            {displayContent && (
              <div style={{
                padding: '0 1.25rem 0.75rem',
                color: '#ffffff',
                fontSize: '15px',
                lineHeight: '1.5',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word'
              }}>
                {displayContent}
              </div>
            )}

            {/* Interaction Bar */}
            <div style={{
              padding: '0.75rem 1.25rem',
              display: 'flex',
              alignItems: 'center',
              gap: '1.25rem'
            }}>
              {/* Like - Heart Icon */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" style={{ display: 'block' }}>
                  <path d="M12 21.35L10.55 20.03C5.4 15.36 2 12.27 2 8.5C2 5.41 4.42 3 7.5 3C9.24 3 10.91 3.81 12 5.08C13.09 3.81 14.76 3 16.5 3C19.58 3 22 5.41 22 8.5C22 12.27 18.6 15.36 13.45 20.03L12 21.35Z"
                    stroke="white"
                    strokeWidth="2"
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                {likes > 0 && <span style={{ color: '#ffffff', fontSize: '15px', lineHeight: '24px' }}>{likes}</span>}
              </div>

              {/* Comment - Speech Bubble */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" style={{ display: 'block' }}>
                  <path d="M21 11.5C21.0034 12.8199 20.6951 14.1219 20.1 15.3C19.3944 16.7118 18.3098 17.8992 16.9674 18.7293C15.6251 19.5594 14.0782 19.9994 12.5 20C11.1801 20.0035 9.87812 19.6951 8.7 19.1L3 21L4.9 15.3C4.30493 14.1219 3.99656 12.8199 4 11.5C4.00061 9.92179 4.44061 8.37488 5.27072 7.03258C6.10083 5.69028 7.28825 4.6056 8.7 3.90003C9.87812 3.30496 11.1801 2.99659 12.5 3.00003H13C15.0843 3.11502 17.053 3.99479 18.5291 5.47089C20.0052 6.94699 20.885 8.91568 21 11V11.5Z"
                    stroke="white"
                    strokeWidth="2"
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                {comments > 0 && <span style={{ color: '#ffffff', fontSize: '15px', lineHeight: '24px' }}>{comments}</span>}
              </div>

              {/* Share - Paper Plane */}
              <div style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" style={{ display: 'block' }}>
                  <path d="M22 2L11 13M22 2L15 22L11 13M22 2L2 9L11 13"
                    stroke="white"
                    strokeWidth="2"
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>

              <div style={{ flex: 1 }} />

              {/* Bookmark */}
              <div style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                <svg width="20" height="24" viewBox="0 0 20 24" fill="none" style={{ display: 'block' }}>
                  <path d="M19 21L10 15L1 21V3C1 2.46957 1.21071 1.96086 1.58579 1.58579C1.96086 1.21071 2.46957 1 3 1H17C17.5304 1 18.0391 1.21071 18.4142 1.58579C18.7893 1.96086 19 2.46957 19 3V21Z"
                    stroke="white"
                    strokeWidth="2"
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </div>
          </div>

          {/* Bottom Navigation */}
          <div style={{
            borderTop: '0.5px solid rgba(255, 255, 255, 0.1)',
            padding: '0.5rem 1.25rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-around',
            background: '#000000',
            paddingBottom: '1.5rem',
            position: 'relative'
          }}>
            <Icon icon="mdi:home" style={{ fontSize: '28px', color: '#ffffff' }} />
            <Icon icon="mdi:magnify" style={{ fontSize: '28px', color: '#ffffff' }} />
            <Icon icon="mdi:plus-box" style={{ fontSize: '28px', color: '#ffffff' }} />
            <Icon icon="mdi:play-box-outline" style={{ fontSize: '28px', color: '#ffffff' }} />
            <div style={{ position: 'relative' }}>
              <div style={{
                width: '28px',
                height: '28px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                border: '2px solid #ffffff'
              }} />
              {/* Notification Badge */}
              <div style={{
                position: 'absolute',
                top: '-2px',
                right: '-2px',
                width: '10px',
                height: '10px',
                borderRadius: '50%',
                background: '#ff3b5c',
                border: '2px solid #000000'
              }} />
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      {showButtons && (
        <div style={{
          display: 'flex',
          gap: '1rem',
          width: '393px',
          maxWidth: '100%'
        }}>
          <button
            onClick={onEdit}
            style={{
              flex: 1,
              padding: '1rem',
              background: '#ffffff',
              color: '#000000',
              border: 'none',
              borderRadius: '12px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
            }}
            onMouseOver={(e) => e.target.style.background = '#f3f4f6'}
            onMouseOut={(e) => e.target.style.background = '#ffffff'}
          >
            Edit Post
          </button>

          <button
            onClick={onPublish}
            style={{
              flex: 1,
              padding: '1rem',
              background: '#3b82f6',
              color: '#ffffff',
              border: 'none',
              borderRadius: '12px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s',
              boxShadow: '0 2px 8px rgba(59, 130, 246, 0.3)'
            }}
            onMouseOver={(e) => e.target.style.background = '#2563eb'}
            onMouseOut={(e) => e.target.style.background = '#3b82f6'}
          >
            Publish Now
          </button>
        </div>
      )}

      <style>
        {`
          @keyframes spin {
            from {
              transform: rotate(0deg);
            }
            to {
              transform: rotate(360deg);
            }
          }
          .spin {
            animation: spin 1s linear infinite;
          }
        `}
      </style>
    </div>
  );
}

export default ThreadsMobilePreview;
